// Imperative MapLibre wrapper. React drives it through plain method calls (see MapView). Handles the
// base map (vector ↔ satellite), field/lane/vehicle layers, field tap-to-select, and the heading-up
// "driving" camera. App layer data is cached so it survives a basemap (setStyle) swap.
import maplibregl, {
  type GeoJSONSource,
  type LngLatBoundsLike,
  type Map,
  type StyleSpecification,
} from 'maplibre-gl';
import type { FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import {
  circle as turfCircle,
  destination as turfDestination,
  bearing as turfBearing,
} from '@turf/turf';
import type { Course, GnssFix, LonLat } from '@fla/core';
import type { FieldCollection, FieldFeature } from '../api';

export type Basemap = 'vector' | 'satellite';
export interface AvoidZone {
  center: LonLat;
  radiusM: number;
}

// Keyless vector basemap for dev. Offline on the Pi → swap for a bundled pmtiles:// source.
const VECTOR_STYLE = 'https://tiles.openfreemap.org/styles/liberty';
// Satellite raster (dev). ESRI World Imagery is fine for prototyping (attribution shown); for a
// shipped offline product, swap for bundled Sentinel-2 tiles. See wiki/reference/research-findings.
const SATELLITE_STYLE: StyleSpecification = {
  version: 8,
  sources: {
    sat: {
      type: 'raster',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
      // ESRI World Imagery only has tiles to ~z19; cap the source there so MapLibre OVERZOOMS
      // (upscales the z19 tile) past it instead of requesting non-existent tiles and going blank.
      maxzoom: 19,
      attribution: 'Imagery © Esri, Maxar, Earthstar Geographics',
    },
  },
  layers: [{ id: 'sat', type: 'raster', source: 'sat' }],
};

const EMPTY: FeatureCollection = { type: 'FeatureCollection', features: [] };
/** Chase-camera vertical bias: fraction of the view height the vehicle sits below centre. */
const DRIVE_OFFSET_FRAC = 0.18;
const SOURCE_IDS = [
  'fields',
  'selected',
  'coverage',
  'headland',
  'lanes',
  'active',
  'zones',
  'trail',
  'draft',
  'arrow',
  'vehicle',
] as const;

function multiLineFC(rings: LonLat[][]): FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: rings
      .filter((r) => r.length >= 2)
      .map((r) => ({
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: r },
      })),
  };
}

function draftFC(ring: LonLat[]): FeatureCollection {
  const features: FeatureCollection['features'] = [];
  if (ring.length >= 2) {
    features.push({
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: ring.length >= 3 ? [...ring, ring[0]!] : ring },
    });
  }
  ring.forEach((p, i) =>
    features.push({
      type: 'Feature',
      properties: { i },
      geometry: { type: 'Point', coordinates: p },
    }),
  );
  return { type: 'FeatureCollection', features };
}

function zonesFC(zones: AvoidZone[]): FeatureCollection {
  return {
    type: 'FeatureCollection',
    // Tag each circle with its source index so a tap can select that exact zone (keep the original
    // index even when some zones are filtered out for having no radius).
    features: zones
      .map((z, i) => ({ z, i }))
      .filter(({ z }) => z.radiusM > 0)
      .map(({ z, i }) => {
        const c = turfCircle(z.center, z.radiusM, { units: 'meters', steps: 48 });
        c.properties = { i };
        return c;
      }),
  };
}

function lineFC(coords: LonLat[]): FeatureCollection {
  if (coords.length < 2) return EMPTY;
  return {
    type: 'FeatureCollection',
    features: [
      { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: coords } },
    ],
  };
}

function polygonFC(geom: Polygon | MultiPolygon | null): FeatureCollection {
  if (!geom) return EMPTY;
  return {
    type: 'FeatureCollection',
    features: [{ type: 'Feature', properties: {}, geometry: geom }],
  };
}

/** Snap a bearing to the nearest of `angles` within `tolDeg`, else return it unchanged. */
function snapToAngle(raw: number, angles: number[], tolDeg: number): number {
  let best = raw;
  let bestDelta = tolDeg;
  for (const a of angles) {
    const cand = ((a % 360) + 360) % 360;
    const delta = Math.abs(((raw - cand + 540) % 360) - 180);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = cand;
    }
  }
  return best;
}

function laneFeatures(course: Course | null): FeatureCollection {
  if (!course) return EMPTY;
  return {
    type: 'FeatureCollection',
    features: course.lanes.map((l) => ({
      type: 'Feature',
      properties: { index: l.index },
      geometry: { type: 'LineString', coordinates: l.coordinates },
    })),
  };
}

function pointFC(fix: GnssFix | null): FeatureCollection {
  if (!fix) return EMPTY;
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: { heading: fix.heading ?? 0 },
        geometry: { type: 'Point', coordinates: [fix.lon, fix.lat] },
      },
    ],
  };
}

function fieldFC(f: FieldFeature | null): FeatureCollection {
  return f ? ({ type: 'FeatureCollection', features: [f] } as FeatureCollection) : EMPTY;
}

export class MapController {
  readonly map: Map;
  private ready = false;
  private queue: Array<() => void> = [];
  private basemap: Basemap = 'vector';
  onField?: (f: FieldFeature) => void;

  // Cached layer data so it survives a setStyle (basemap) swap.
  private handles: maplibregl.Marker[] = [];
  private zoneHandle: maplibregl.Marker | null = null;
  /** Fired on any map click (lon/lat). The store decides what to do based on the active edit mode. */
  onMapClick?: (p: LonLat) => void;
  /** Fired when a tap lands on an existing zone (its index), so it can be selected for edit/delete. */
  onZoneClick?: (index: number) => void;
  private cache: Record<(typeof SOURCE_IDS)[number], FeatureCollection> = {
    fields: EMPTY,
    selected: EMPTY,
    coverage: EMPTY,
    headland: EMPTY,
    lanes: EMPTY,
    active: EMPTY,
    zones: EMPTY,
    trail: EMPTY,
    draft: EMPTY,
    arrow: EMPTY,
    vehicle: EMPTY,
  };
  private arrowMarker: maplibregl.Marker | null = null;
  private shiftHeld = false;
  private readonly onKey = (e: KeyboardEvent) => {
    this.shiftHeld = e.shiftKey;
  };

  // Driving chase-camera follow state. A user pan pauses auto-follow for a few seconds; it then
  // smoothly resumes following the vehicle. `onFollowChange` lets React reflect the state (e.g. the
  // locate button highlights as a "re-center" affordance while panned away).
  private followPausedUntil = 0;
  private following = true;
  private needInitialDriveZoom = false;
  /** True while a user zoom/rotate/pitch gesture is mid-flight — chase easeTo is suspended so it
   *  doesn't fight the gesture (set by *start with originalEvent, cleared by *end). */
  private userAdjusting = false;
  onFollowChange?: (following: boolean) => void;
  private static readonly FOLLOW_RESUME_MS = 4000;
  private static readonly DRIVE_ZOOM = 18;

  constructor(container: HTMLElement, center: [number, number]) {
    this.map = new maplibregl.Map({
      container,
      style: VECTOR_STYLE,
      center,
      zoom: 15,
      attributionControl: { compact: true },
      dragRotate: false,
    });
    // Fires on the initial style AND after every setStyle — re-add app layers each time.
    this.map.on('style.load', () => {
      this.installLayers();
      this.ready = true;
      const q = this.queue;
      this.queue = [];
      q.forEach((fn) => fn());
    });
    this.map.on('click', (e) => {
      // A tap on an existing zone selects it (resize/move/delete) instead of dropping a new zone.
      if (this.onZoneClick && this.map.getLayer('zones-fill')) {
        const hit = this.map.queryRenderedFeatures(e.point, { layers: ['zones-fill'] })[0];
        const i = hit?.properties?.i;
        if (typeof i === 'number') {
          this.onZoneClick(i);
          return;
        }
      }
      this.onMapClick?.([e.lngLat.lng, e.lngLat.lat]);
    });
    // Only a PAN (drag) detaches chase-follow — zoom/rotate keep following so the user can
    // scroll/pinch-zoom out while staying locked on the tractor. A user gesture has originalEvent;
    // our programmatic easeTo does not.
    const onUserPan = (e: { originalEvent?: unknown }) => {
      if (!e.originalEvent) return;
      this.followPausedUntil = performance.now() + MapController.FOLLOW_RESUME_MS;
      if (this.following) {
        this.following = false;
        this.onFollowChange?.(false);
      }
    };
    this.map.on('dragstart', onUserPan);
    // While a user zoom/rotate gesture is in flight, suspend the chase easeTo so it doesn't preempt
    // (and stutter) the user's animation every fix — without detaching follow. Re-centres on the
    // tractor once the gesture ends. Only user gestures carry originalEvent (our easeTo doesn't).
    const onAdjustStart = (e: { originalEvent?: unknown }) => {
      if (e.originalEvent) this.userAdjusting = true;
    };
    const onAdjustEnd = () => {
      this.userAdjusting = false;
    };
    this.map.on('zoomstart', onAdjustStart);
    this.map.on('rotatestart', onAdjustStart);
    this.map.on('pitchstart', onAdjustStart);
    this.map.on('zoomend', onAdjustEnd);
    this.map.on('rotateend', onAdjustEnd);
    this.map.on('pitchend', onAdjustEnd);
    window.addEventListener('keydown', this.onKey);
    window.addEventListener('keyup', this.onKey);
  }

  private whenReady(fn: () => void): void {
    if (this.ready) fn();
    else this.queue.push(fn);
  }

  private src(id: string): GeoJSONSource {
    return this.map.getSource(id) as GeoJSONSource;
  }

  /** Build the vehicle heading-arrow icon on a canvas and register it (idempotent; re-added after a
   *  basemap setStyle wipes registered images). Points "up" (north); icon-rotate spins it to heading. */
  private addVehicleArrowImage(): void {
    if (this.map.hasImage('vehicle-arrow')) return;
    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.translate(size / 2, size / 2);
    ctx.beginPath();
    ctx.moveTo(0, -26); // tip (forward)
    ctx.lineTo(18, 22); // right base
    ctx.lineTo(0, 12); // tail notch
    ctx.lineTo(-18, 22); // left base
    ctx.closePath();
    ctx.fillStyle = '#22c55e';
    ctx.fill();
    ctx.lineJoin = 'round';
    ctx.lineWidth = 4;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();
    this.map.addImage('vehicle-arrow', ctx.getImageData(0, 0, size, size), { pixelRatio: 2 });
  }

  private installLayers(): void {
    for (const id of SOURCE_IDS) {
      this.map.addSource(id, { type: 'geojson', data: this.cache[id] });
    }
    // Dark casing under each colored line so overlays read on BOTH the vector map and satellite.
    this.map.addLayer({
      id: 'fields-fill',
      type: 'fill',
      source: 'fields',
      paint: { 'fill-color': '#22c55e', 'fill-opacity': 0.12 },
    });
    this.map.addLayer({
      id: 'fields-outline',
      type: 'line',
      source: 'fields',
      paint: { 'line-color': '#fafafa', 'line-width': 2, 'line-opacity': 0.9 },
    });
    this.map.addLayer({
      id: 'selected-fill',
      type: 'fill',
      source: 'selected',
      paint: { 'fill-color': '#22c55e', 'fill-opacity': 0.3 },
    });
    this.map.addLayer({
      id: 'selected-casing',
      type: 'line',
      source: 'selected',
      paint: { 'line-color': '#052e16', 'line-width': 6, 'line-opacity': 0.6 },
    });
    this.map.addLayer({
      id: 'selected-outline',
      type: 'line',
      source: 'selected',
      paint: { 'line-color': '#4ade80', 'line-width': 3 },
    });
    // Worked-area coverage strip — painted as you drive, below the lanes so guidance stays on top.
    // Teal (distinct from the green field, sky-blue trail line, and yellow guidance) = "done".
    this.map.addLayer({
      id: 'coverage-fill',
      type: 'fill',
      source: 'coverage',
      paint: { 'fill-color': '#2dd4bf', 'fill-opacity': 0.4 },
    });
    this.map.addLayer({
      id: 'lanes-casing',
      type: 'line',
      source: 'lanes',
      paint: { 'line-color': '#052e16', 'line-width': 4, 'line-opacity': 0.55 },
    });
    this.map.addLayer({
      id: 'lanes-line',
      type: 'line',
      source: 'lanes',
      paint: { 'line-color': '#86efac', 'line-width': 1.8 },
    });
    this.map.addLayer({
      id: 'headland-line',
      type: 'line',
      source: 'headland',
      paint: { 'line-color': '#fb923c', 'line-width': 2, 'line-dasharray': [3, 2] },
    });
    this.map.addLayer({
      id: 'active-casing',
      type: 'line',
      source: 'active',
      layout: { 'line-cap': 'round' },
      paint: { 'line-color': '#000000', 'line-width': 7, 'line-opacity': 0.5 },
    });
    this.map.addLayer({
      id: 'active-line',
      type: 'line',
      source: 'active',
      layout: { 'line-cap': 'round' },
      paint: { 'line-color': '#fde047', 'line-width': 4 },
    });
    // Driven trail sits ABOVE the planned/active lane so you can see where you actually went.
    this.map.addLayer({
      id: 'trail-line',
      type: 'line',
      source: 'trail',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#38bdf8', 'line-width': 3.5, 'line-opacity': 0.95 },
    });
    this.map.addLayer({
      id: 'zones-fill',
      type: 'fill',
      source: 'zones',
      paint: { 'fill-color': '#dc2626', 'fill-opacity': 0.5 },
    });
    this.map.addLayer({
      id: 'zones-line',
      type: 'line',
      source: 'zones',
      paint: { 'line-color': '#fecaca', 'line-width': 2.5 },
    });
    this.map.addLayer({
      id: 'draft-line',
      type: 'line',
      source: 'draft',
      filter: ['==', '$type', 'LineString'],
      paint: { 'line-color': '#fde047', 'line-width': 2, 'line-dasharray': [2, 1.5] },
    });
    this.map.addLayer({
      id: 'draft-verts',
      type: 'circle',
      source: 'draft',
      filter: ['==', '$type', 'Point'],
      paint: {
        'circle-radius': 6,
        'circle-color': '#fde047',
        'circle-stroke-color': '#000000',
        'circle-stroke-width': 1.5,
      },
    });
    this.map.addLayer({
      id: 'arrow-line',
      type: 'line',
      source: 'arrow',
      layout: { 'line-cap': 'round' },
      paint: { 'line-color': '#22d3ee', 'line-width': 4 },
    });
    // Small anchor dot under the heading arrow — marks the exact position and stays visible when the
    // arrow is tiny (zoomed far out).
    this.map.addLayer({
      id: 'vehicle-dot',
      type: 'circle',
      source: 'vehicle',
      paint: {
        'circle-radius': 4,
        'circle-color': '#15803d',
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1.5,
      },
    });
    // Heading arrow that lies ON the ground plane (pitch-aligned) and rotates to the true heading, so
    // it reads as a vehicle in the 3D driving view AND as a facing arrow in the top-down overview.
    this.addVehicleArrowImage();
    this.map.addLayer({
      id: 'vehicle-arrow',
      type: 'symbol',
      source: 'vehicle',
      layout: {
        'icon-image': 'vehicle-arrow',
        'icon-size': 1,
        'icon-rotate': ['get', 'heading'],
        'icon-rotation-alignment': 'map',
        'icon-pitch-alignment': 'map',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    });

    this.map.on('click', 'fields-fill', (e) => {
      const f = e.features?.[0];
      if (f && this.onField) {
        this.onField({
          type: 'Feature',
          properties: (f.properties ?? {}) as Record<string, unknown>,
          geometry: f.geometry as FieldFeature['geometry'],
        });
      }
    });
    this.map.on('mouseenter', 'fields-fill', () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });
    this.map.on('mouseleave', 'fields-fill', () => {
      this.map.getCanvas().style.cursor = '';
    });
  }

  private set(id: (typeof SOURCE_IDS)[number], data: FeatureCollection): void {
    this.cache[id] = data;
    this.whenReady(() => this.src(id).setData(data));
  }

  setBasemap(kind: Basemap): void {
    if (kind === this.basemap) return;
    this.basemap = kind;
    this.ready = false; // queue updates until the new style + layers are installed
    // diff:false forces a full reload so 'style.load' re-fires and our app layers get re-installed.
    this.map.setStyle(kind === 'satellite' ? SATELLITE_STYLE : VECTOR_STYLE, { diff: false });
  }

  getBasemap(): Basemap {
    return this.basemap;
  }

  setFields(fc: FieldCollection | null): void {
    this.set('fields', (fc ?? EMPTY) as FeatureCollection);
  }
  setSelected(f: FieldFeature | null): void {
    this.set('selected', fieldFC(f));
  }
  setCourse(course: Course | null): void {
    this.set('lanes', laneFeatures(course));
    this.set('active', EMPTY);
  }
  /** Highlight just the upcoming stretch of the active lane (lon/lat polyline). Empty clears it. */
  setActiveSegment(coords: LonLat[]): void {
    this.set('active', lineFC(coords));
  }
  /** Headland boundary ring(s) — the turning margin at the field edge. */
  setHeadland(rings: LonLat[][]): void {
    this.set('headland', multiLineFC(rings));
  }

  /**
   * A draggable direction arrow at `centroid` pointing along `headingDeg`. The shaft snaps to the
   * sensible `snapAngles` (field edges + perpendiculars) WHILE dragging (hold Shift to override);
   * `onRotate(bearing)` commits on release. Pass null to hide it.
   */
  setHeadingArrow(
    centroid: LonLat | null,
    headingDeg: number,
    snapAngles: number[],
    onRotate?: (bearing: number) => void,
  ): void {
    this.whenReady(() => {
      if (this.arrowMarker) {
        this.arrowMarker.remove();
        this.arrowMarker = null;
      }
      if (!centroid) {
        this.src('arrow').setData(EMPTY);
        return;
      }
      const RADIUS_KM = 0.05;
      const place = (brg: number): LonLat => {
        const tip = turfDestination(centroid, RADIUS_KM, brg).geometry.coordinates as LonLat;
        this.src('arrow').setData(lineFC([centroid, tip]));
        return tip;
      };
      place(headingDeg);
      const el = document.createElement('div');
      el.className = 'fla-arrow';
      const marker = new maplibregl.Marker({ element: el, draggable: true })
        .setLngLat(turfDestination(centroid, RADIUS_KM, headingDeg).geometry.coordinates as LonLat)
        .addTo(this.map);
      const snapped = (): number => {
        const ll = marker.getLngLat();
        let raw = turfBearing(centroid, [ll.lng, ll.lat]);
        if (raw < 0) raw += 360;
        return this.shiftHeld ? raw : snapToAngle(raw, snapAngles, 10);
      };
      // While dragging: snap the shaft live (the marker follows the finger; the line jumps to angle).
      marker.on('drag', () => place(snapped()));
      // On release: snap the marker to the angle too and commit.
      marker.on('dragend', () => {
        const b = snapped();
        marker.setLngLat(place(b));
        onRotate?.(b);
      });
      this.arrowMarker = marker;
    });
  }
  setVehicle(fix: GnssFix | null): void {
    this.set('vehicle', pointFC(fix));
  }
  /** The breadcrumb of actually-driven positions. */
  setTrail(coords: LonLat[]): void {
    this.set('trail', lineFC(coords));
  }
  /** The worked-area strip (clipped to the field). Pass null to clear. */
  setCoverage(geom: Polygon | MultiPolygon | null): void {
    this.set('coverage', polygonFC(geom));
  }
  /** The in-progress drawn outline (vertices + closing line). */
  setDraft(ring: LonLat[]): void {
    this.set('draft', draftFC(ring));
  }
  /** Avoidance (no-drive) zones. */
  setZones(zones: AvoidZone[]): void {
    this.set('zones', zonesFC(zones));
  }
  /** Draggable vertex handles for editing a field outline. Drag moves a vertex; a tap (no drag)
   *  deletes it. Pass null to clear. */
  setEditHandles(
    coords: LonLat[] | null,
    onDrag?: (index: number, p: LonLat) => void,
    onDelete?: (index: number) => void,
  ): void {
    for (const m of this.handles) m.remove();
    this.handles = [];
    if (!coords) return;
    coords.forEach((p, i) => {
      const el = document.createElement('div');
      el.className = 'fla-handle';
      const marker = new maplibregl.Marker({ element: el, draggable: true })
        .setLngLat(p)
        .addTo(this.map);
      let moved = false;
      marker.on('dragstart', () => {
        moved = false;
      });
      marker.on('drag', () => {
        moved = true;
        const ll = marker.getLngLat();
        onDrag?.(i, [ll.lng, ll.lat]);
      });
      // A plain tap (no drag) deletes the vertex. The `moved` flag rejects the click that fires at
      // the end of a drag.
      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        if (!moved) onDelete?.(i);
      });
      this.handles.push(marker);
    });
  }

  /** A single draggable handle at a zone's center, for moving the selected zone. Pass null to clear. */
  setZoneHandle(center: LonLat | null, onDrag?: (p: LonLat) => void): void {
    this.zoneHandle?.remove();
    this.zoneHandle = null;
    if (!center) return;
    const el = document.createElement('div');
    el.className = 'fla-handle';
    const marker = new maplibregl.Marker({ element: el, draggable: true })
      .setLngLat(center)
      .addTo(this.map);
    marker.on('drag', () => {
      const ll = marker.getLngLat();
      onDrag?.([ll.lng, ll.lat]);
    });
    this.zoneHandle = marker;
  }

  /** Heading-up chase camera. The vehicle sits below centre, leaving room up top for the HUD. While
   *  the user is panning/zooming (follow paused) this is a no-op so their gesture isn't fought; it
   *  resumes automatically once the pause window lapses (or on resumeFollow()). */
  driveCamera(fix: GnssFix): void {
    this.whenReady(() => {
      // Don't stomp an in-flight user zoom/rotate; the next fix after the gesture re-centres.
      if (this.userAdjusting) return;
      if (performance.now() < this.followPausedUntil) return;
      if (!this.following) {
        this.following = true;
        this.onFollowChange?.(true);
      }
      // Push the vehicle below centre by this fraction of the view, leaving room up top for the HUD.
      const oy = this.map.getContainer().clientHeight * DRIVE_OFFSET_FRAC;
      // Force the chase zoom only on the first driving frame; afterwards keep the user's zoom level.
      const zoom = this.needInitialDriveZoom ? MapController.DRIVE_ZOOM : undefined;
      this.needInitialDriveZoom = false;
      this.map.easeTo({
        center: [fix.lon, fix.lat],
        bearing: fix.heading ?? this.map.getBearing(),
        pitch: 50,
        ...(zoom != null ? { zoom } : {}),
        offset: [0, oy],
        duration: 250,
      });
    });
  }

  /** Enter driving: next chase frame snaps to the drive zoom; follow is active. */
  beginDriving(): void {
    this.needInitialDriveZoom = true;
    this.followPausedUntil = 0;
    if (!this.following) {
      this.following = true;
      this.onFollowChange?.(true);
    }
  }

  /** Immediately resume chase-follow (e.g. a "re-center" button), centring on the given fix. */
  resumeFollow(fix: GnssFix | null): void {
    this.followPausedUntil = 0;
    if (fix) this.driveCamera(fix);
    else if (!this.following) {
      this.following = true;
      this.onFollowChange?.(true);
    }
  }

  /** North-up overview camera. */
  overviewCamera(): void {
    this.whenReady(() => this.map.easeTo({ bearing: 0, pitch: 0, duration: 300 }));
  }

  /** Center on a fix without entering the chase camera — the "locate me" button outside driving. */
  flyToFix(fix: GnssFix): void {
    this.whenReady(() =>
      this.map.easeTo({
        center: [fix.lon, fix.lat],
        zoom: Math.max(this.map.getZoom(), 16),
        duration: 500,
      }),
    );
  }

  setFieldsVisible(visible: boolean): void {
    this.whenReady(() => {
      const v = visible ? 'visible' : 'none';
      for (const id of ['fields-fill', 'fields-outline']) {
        if (this.map.getLayer(id)) this.map.setLayoutProperty(id, 'visibility', v);
      }
    });
  }

  fitTo(fc: FieldCollection): void {
    this.whenReady(() => {
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const f of fc.features) {
        for (const ring of f.geometry.coordinates) {
          for (const pt of ring) {
            const x = pt[0];
            const y = pt[1];
            if (x === undefined || y === undefined) continue;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
      if (Number.isFinite(minX)) {
        const bounds: LngLatBoundsLike = [
          [minX, minY],
          [maxX, maxY],
        ];
        this.map.fitBounds(bounds, { padding: 80, maxZoom: 17, duration: 400 });
      }
    });
  }

  /** Ground resolution at `lat` for the current zoom — used to turn a pixel tap tolerance into
   *  meters (Web Mercator). */
  metersPerPixel(lat: number): number {
    return (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, this.map.getZoom());
  }

  /** Current map viewport as a [minLon,minLat,maxLon,maxLat] bbox. */
  bbox(): [number, number, number, number] {
    const b = this.map.getBounds();
    return [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
  }

  destroy(): void {
    for (const m of this.handles) m.remove();
    this.handles = [];
    this.zoneHandle?.remove();
    this.zoneHandle = null;
    this.arrowMarker?.remove();
    this.arrowMarker = null;
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('keyup', this.onKey);
    this.map.remove();
  }
}
